import {
	useTranslationCapTable,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { ExcelFormat } from '@dealstack/shared/lib/common/file/file';
import { CapExportMenu } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChartPageHeader/CapExportMenu.tsx';
import { CapPageHeader } from '@dealstack/frontend/app/pages/Project/Cap/common/CapPageHeader/CapPageHeader.tsx';
import { CapTableSettings } from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/CapTableSettings/CapTableSettings.tsx';
import { useDownloadCapTableExcel } from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/CapTablePageHeader/useDownloadCapTableExcel.ts';
import { useDownloadCapTableJson } from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/CapTablePageHeader/useDownloadCapTableJson.ts';
import { Dispatch, RefObject, SetStateAction } from 'react';
import {
	CAP_TABLE_VALUE_DISPLAY_MODE,
	CAP_TABLE_VALUES_HELD_TYPE,
	CAP_TABLE_VIEW_TYPE,
} from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/tableSettings.ts';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { CapTableRail, PermissionRole, ResourceType } from '@dealstack/shared/lib/generated';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string.ts';
import { PermissionWrapper } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import { Text, Tooltip } from '@mantine/core';
import { TitleRailToggle } from '@dealstack/frontend/app/pages/Project/Cap/common/TitleRailToggle/TitleRailToggle.tsx';
import { AdvancedSwitch } from '@dealstack/frontend/shared/components/Switch/AdvancedSwitch.tsx';

interface Props {
	dealName: string;
	dealId: string;
	tableAllRowsLength: number;
	tableAllColumnsLength: number;
	tableWrapperRef: RefObject<HTMLDivElement>;
	tableRef: RefObject<HTMLTableElement>;
	setIsDownloadingPdf: Dispatch<SetStateAction<boolean>>;
	setIsDownloadingExcel: Dispatch<SetStateAction<boolean>>;
	valuesHeldType: CAP_TABLE_VALUES_HELD_TYPE;
	setValueHeldType: Dispatch<SetStateAction<CAP_TABLE_VALUES_HELD_TYPE>>;
	showSummarized: boolean;
	setShowSummarized: Dispatch<SetStateAction<boolean>>;
	valueDisplayMode: CAP_TABLE_VALUE_DISPLAY_MODE;
	setValueDisplayMode: Dispatch<SetStateAction<CAP_TABLE_VALUE_DISPLAY_MODE>>;
	tableView: CAP_TABLE_VIEW_TYPE;
	setTableView: Dispatch<SetStateAction<CAP_TABLE_VIEW_TYPE>>;
	showPercentages: boolean;
	setShowRowsCheckboxes: Dispatch<SetStateAction<boolean>>;
	showRowsCheckboxes: boolean;
	isFundDeal?: boolean;
	isMasterView?: boolean;
	setIsMasterView?: Dispatch<SetStateAction<boolean>>;
	rail: CapTableRail;
	setRail: Dispatch<SetStateAction<CapTableRail>>;
	hasNomineeStructure: boolean;
}

export const CapTablePageHeader = ({
	dealName,
	dealId,
	setIsDownloadingExcel,
	valuesHeldType,
	setValueHeldType,
	showSummarized,
	setShowSummarized,
	valueDisplayMode,
	setValueDisplayMode,
	tableView,
	setTableView,
	showPercentages,
	setShowRowsCheckboxes,
	showRowsCheckboxes,
	isFundDeal,
	isMasterView,
	setIsMasterView,
	rail,
	setRail,
	hasNomineeStructure,
}: Props) => {
	const tCapTable = useTranslationCapTable();
	const tCommonUi = useTranslationCommonUi();

	const { onDownloadCapTableToExcel } = useDownloadCapTableExcel({
		dealName,
		dealId,
		rail,
		setIsDownloadingExcel,
	});
	const { value: exportDealHoldingsJsonFlag } = useDealstackFeatureFlag(
		FEATURE_FLAG.EXPORT_DEAL_HOLDINGS_JSON,
		{ dealId }
	);
	const { onDownloadCapTableToJson, isDownloadingJson } = useDownloadCapTableJson({
		dealId,
		rail,
	});

	return (
		<CapPageHeader title={tCapTable('title')}>
			{hasNomineeStructure && <TitleRailToggle rail={rail} setRail={setRail} />}

			{isFundDeal && setIsMasterView && (
				<Tooltip label="View aggregated data across all portfolio deals">
					<AdvancedSwitch
						rightText={
							<Text data-testid={TEST_ELEMENT_IDS.FUND_MASTER_VIEW_TOGGLE_LABEL}>
								{tCommonUi('master_view')}
							</Text>
						}
						selectedValue={isMasterView}
						onChange={(value) => setIsMasterView(value === true)}
						selectedColor="dark.8"
						unselectedColor="dark.8"
						dataTestId={TEST_ELEMENT_IDS.FUND_MASTER_VIEW_TOGGLE}
					/>
				</Tooltip>
			)}

			<PermissionWrapper
				resourceType={ResourceType.DEAL}
				resourceId={defaultToEmptyString(dealId)}
				permissionRole={PermissionRole.EXPORT_VIEWER}
			>
				<CapExportMenu
					options={[
						{
							label: tCapTable('download_xlsx'),
							onClick: () => onDownloadCapTableToExcel(),
							dataTestId: TEST_ELEMENT_IDS.CAP_TABLE_DOWNLOAD_XLSX_BUTTON,
						},
						{
							label: tCapTable('download_csv'),
							onClick: () => onDownloadCapTableToExcel(ExcelFormat.CSV),
							dataTestId: TEST_ELEMENT_IDS.CAP_TABLE_DOWNLOAD_CSV_BUTTON,
						},
						...(exportDealHoldingsJsonFlag
							? [
									{
										label: 'Download JSON',
										onClick: onDownloadCapTableToJson,
										isLoading: isDownloadingJson,
										dataTestId: TEST_ELEMENT_IDS.CAP_TABLE_DOWNLOAD_JSON_BUTTON,
									},
								]
							: []),
					]}
				/>
			</PermissionWrapper>

			<CapTableSettings
				valuesHeldType={valuesHeldType}
				setValueHeldType={setValueHeldType}
				showSummarized={showSummarized}
				setSummarized={setShowSummarized}
				valueDisplayMode={valueDisplayMode}
				setValueDisplayMode={setValueDisplayMode}
				tableView={tableView}
				setTableView={setTableView}
				showPercentages={showPercentages}
				setSpecifySecurities={setShowRowsCheckboxes}
				showRowsCheckboxes={showRowsCheckboxes}
			/>
		</CapPageHeader>
	);
};
